import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import LanguageSwitcher from "./LanguageSwitcher";

const Header = () => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isRTL = i18n.dir() === "rtl";

  // Dropdown-Stabilität: Refs + Delay
  const dropdownRef = useRef(null);
  const toggleTimeoutRef = useRef(null);
  useEffect(() => {
    return () => {
      if (toggleTimeoutRef.current) clearTimeout(toggleTimeoutRef.current);
    };
  }, []);

  // Einheitlich ?lang beibehalten
  const getSearchWithLang = () => {
    const params = new URLSearchParams(location.search);
    if (!params.get("lang")) params.set("lang", i18n.language);
    return `?${params.toString()}`;
  };

  // Active-State Helper
  const isActiveHref = (href) => {
    const { pathname, hash } = location;

    // reine Hash-Section (z.B. "#contact" aus Menü)
    if (href.startsWith("#")) {
      const id = href.slice(1);
      return pathname === "/" && hash === `#${id}`;
    }

    // Home-Sections "/#accueil", "/#philosophie", "/#domaines"
    if (href.startsWith("/#")) {
      const id = href.split("#")[1];
      if (pathname !== "/") return false;
      // "Accueil" auch aktiv, wenn kein Hash gesetzt ist
      if (id === "accueil" && (!hash || hash === "#accueil")) return true;
      return hash === `#${id}`;
    }

    // Normale Seiten
    return pathname === href;
  };

  // Desktop Menu Items
  const menuItems = [
    { name: t("nav.home"), href: "/#accueil" },
    { name: t("nav.about"), href: "/about" },
    {
      name: t("nav.domains"),
      href: "/#domaines",
      hasDropdown: true,
      subItems: [
        {
          name: t("domains.domain1.shortTitle"),
          href: "/art-d-etre-soi",
        },
        {
          name: t("domains.domain2.shortTitle"),
          href: "/aube-parentale",
        },
        {
          name: t("domains.domain3.shortTitle"),
          href: "/architecte-ancrage",
        },
      ],
    },
    { name: t("nav.vision"), href: "/#philosophie" },
    { name: t("nav.investment"), href: "/investment" },
    { name: t("nav.contact"), href: "#contact" },
  ];

  // Services-Parent aktiv, wenn auf einer Service-Seite oder auf /#domaines
  const isAnyServiceActive = ["/art-d-etre-soi", "/aube-parentale", "/architecte-ancrage"].includes(
    location.pathname
  );

  const navItemClass = (active) =>
    `text-sm font-medium tracking-wide transition-colors duration-300 ${
      active
        ? "text-warm-sage border-b-2 border-warm-sage pb-1"
        : "text-charcoal hover:text-warm-sage"
    }`;

  const subItemClass = (active) =>
    `block px-4 py-2 text-sm transition-colors ${
      active ? "text-warm-sage bg-warm-sage/10" : "text-charcoal/80 hover:text-warm-sage hover:bg-warm-sage/10"
    }`;

  const handleScroll = (href) => {
    const search = location.search || `?lang=${i18n.language}`;

    // Interne Sections
    if (href.startsWith("/#") || href.startsWith("#")) {
      const id = href.replace("/#", "").replace("#", "");
      if (location.pathname !== "/") {
        navigate({ pathname: "/", search, hash: `#${id}` });
        return;
      }
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
      return;
    }

    // Normale Seiten
    navigate({ pathname: href, search });
  };

  // Dropdown-Open/Close mit kleinem Delay (gegen Flackern)
  const openDropdown = () => {
    if (toggleTimeoutRef.current) clearTimeout(toggleTimeoutRef.current);
    setIsDropdownOpen(true);
  };
  const closeDropdown = () => {
    if (toggleTimeoutRef.current) clearTimeout(toggleTimeoutRef.current);
    toggleTimeoutRef.current = setTimeout(() => setIsDropdownOpen(false), 120);
  };
  const handleBlurWithin = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.relatedTarget)) {
      closeDropdown();
    }
  };

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 h-16 md:h-20
                   bg-cream-white/80 backdrop-blur supports-[backdrop-filter]:bg-cream-white/60
                   border-b border-warm-gray/10"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
      aria-label="Site header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
        <div className="flex items-center justify-between h-full">
          {/* Logo */}
          <Link to={{ pathname: "/", search: getSearchWithLang() }} className="flex items-center" onClick={() => setIsMenuOpen(false)}>
            <div className="relative inline-block text-center select-none leading-none">
              <h1 className="text-2xl md:text-3xl font-serif font-light tracking-tight text-charcoal">
                Mind<span className="text-warm-sage">’UP</span>
              </h1>
              <div className="mt-0.5 md:mt-1">
                <div className="h-px w-8 mx-auto bg-warm-sage/60 rounded-full" />
                <p
                  className="mt-1 text-[10px] md:text-[9px] uppercase tracking-[0.16em]
                              leading-none text-warm-gray/90 font-medium"
                  style={{
                    fontFamily:
                      "'Inter','Poppins',system-ui,-apple-system,Segoe UI,Roboto','Helvetica Neue',Arial,sans-serif",
                  }}
                >
                  Coaching&nbsp;&middot;&nbsp;Consulting
                </p>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav
            className="hidden md:flex items-center gap-8 lg:gap-10 xl:gap-12 whitespace-nowrap"
            aria-label="Primary"
          >
            {menuItems.map((item) => {
              if (item.hasDropdown) {
                const parentActive = isActiveHref(item.href) || isAnyServiceActive;
                return (
                  <div
                    key={item.name}
                    className="relative"
                    ref={dropdownRef}
                    onMouseEnter={openDropdown}
                    onMouseLeave={closeDropdown}
                    onBlur={handleBlurWithin}
                  >
                    <button
                      className={`flex items-center gap-1 ${navItemClass(parentActive)}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setIsDropdownOpen(false);
                        handleScroll("/#domaines");
                      }}
                      onFocus={openDropdown}
                      aria-haspopup="menu"
                      aria-expanded={isDropdownOpen}
                      aria-controls="services-menu"
                      type="button"
                    >
                      {item.name} <ChevronDown className="h-4 w-4 mt-[1px]" />
                    </button>

                    {isDropdownOpen && (
                      <ul
                        id="services-menu"
                        role="menu"
                        tabIndex={-1}
                        className="absolute left-0 mt-2 w-64 bg-white border border-soft-gray/20 shadow-lg rounded-xl py-2 z-50"
                      >
                        {item.subItems.map((sub) => {
                          const active = isActiveHref(sub.href);
                          return (
                            <li key={sub.href}>
                              <Link
                                to={{ pathname: sub.href, search: getSearchWithLang() }}
                                role="menuitem"
                                tabIndex={0}
                                onClick={() => setIsDropdownOpen(false)}
                                className={subItemClass(active)}
                              >
                                {sub.name}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              }

              const active = isActiveHref(item.href);
              return (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleScroll(item.href);
                  }}
                  className={navItemClass(active)}
                >
                  {item.name}
                </a>
              );
            })}
          </nav>

          {/* Desktop CTA (kleiner gemacht) */}
          <div className="hidden md:flex items-center gap-4">
            <LanguageSwitcher />
            <Button
              onClick={() => handleScroll("/#contact")}
              className="bg-warm-sage hover:bg-warm-sage/90 text-cream-white font-medium px-4 py-1.5 text-sm rounded-md transition-all duration-300"
            >
              {t("nav.cta")}
            </Button>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setIsMenuOpen((v) => !v)}
            className="md:hidden text-charcoal hover:text-warm-sage transition-colors"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div
          className="md:hidden fixed inset-x-0 bg-cream-white/95 backdrop-blur border-t border-warm-gray/10"
          style={{ top: "calc(env(safe-area-inset-top) + 4rem)" }}
          role="dialog"
          aria-modal="true"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav
              className={`flex flex-col ${
                isRTL ? "items-end text-right" : "items-start text-left"
              } gap-4`}
            >
              {menuItems.map((item) =>
                item.hasDropdown ? (
                  <div key={item.name} className="w-full">
                    <button
                      onClick={() => setIsDropdownOpen((v) => !v)}
                      className={`flex items-center justify-between w-full text-base font-medium transition-colors py-1.5 ${
                        isActiveHref(item.href) || isAnyServiceActive
                          ? "text-warm-sage"
                          : "text-charcoal hover:text-warm-sage"
                      }`}
                      type="button"
                    >
                      {item.name}
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${
                          isDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {isDropdownOpen && (
                      <div className={`${isRTL ? "mr-4" : "ml-4"} mt-1 space-y-1 w-full`}>
                        {item.subItems.map((sub) => (
                          <Link
                            key={sub.href}
                            to={{ pathname: sub.href, search: getSearchWithLang() }}
                            onClick={() => setIsMenuOpen(false)}
                            className={`block text-sm transition ${
                              isActiveHref(sub.href)
                                ? "text-warm-sage"
                                : "text-charcoal/80 hover:text-warm-sage"
                            }`}
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      setIsMenuOpen(false);
                      handleScroll(item.href);
                    }}
                    className={`w-full text-base font-medium transition-colors py-1.5 ${
                      isActiveHref(item.href) ? "text-warm-sage" : "text-charcoal hover:text-warm-sage"
                    }`}
                  >
                    {item.name}
                  </a>
                )
              )}

              <div
                className={`flex w-full items-center justify-between gap-3 pt-4 mt-2 border-t border-warm-gray/10 ${
                  isRTL ? "flex-row-reverse" : ""
                }`}
              >
                <LanguageSwitcher />
                <Button
                  onClick={() => {
                    setIsMenuOpen(false);
                    handleScroll("/#contact");
                  }}
                  className="bg-warm-sage hover:bg-warm-sage/90 text-cream-white text-sm px-5 py-2 rounded-md"
                >
                  {t("nav.cta")}
                </Button>
              </div>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
